import React, { useState } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import { SUCCESS_STORIES, LANDING_COLORS } from '../../content/landingContent';

/** Pill buttons — mobile only, switch between brand stories */
const BrandPills = ({ activeIndex, onChange }) => (
  <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', gap: 1.5, mb: 3 }}>
    {SUCCESS_STORIES.map((story, index) => {
      const isActive = activeIndex === index;
      return (
        <Button
          key={story.id}
          onClick={() => onChange(index)}
          sx={{
            borderRadius: '50px',
            px: 3,
            py: 0.75,
            minWidth: 100,
            textTransform: 'none',
            fontFamily: 'Cabin, sans-serif',
            fontWeight: 600,
            fontSize: 14,
            bgcolor: isActive ? LANDING_COLORS.purple : '#fff',
            color: isActive ? '#fff' : LANDING_COLORS.purple,
            border: isActive ? 'none' : `1px solid ${LANDING_COLORS.borderLight}`,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: isActive ? LANDING_COLORS.purpleDark : '#fafafa',
              boxShadow: 'none',
            },
          }}
        >
          {story.brand}
        </Button>
      );
    })}
  </Box>
);

/** Single metric column — label on top, bordered box below */
const MetricColumn = ({ label, sub, value, fullWidth = false }) => (
  <Box sx={{ flex: fullWidth ? '1 1 100%' : '1 1 0', minWidth: fullWidth ? '100%' : 0 }}>
    <Typography
      sx={{
        fontFamily: 'Cabin, sans-serif',
        fontSize: 14,
        color: LANDING_COLORS.textGray,
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        border: `1px solid ${LANDING_COLORS.borderLight}`,
        borderRadius: '6px',
        px: 1.5,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 41,
      }}
    >
      <Typography sx={{ fontFamily: 'Cabin', fontSize: 14, color: LANDING_COLORS.textGray }}>
        {sub}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Cabin, sans-serif',
          fontWeight: 700,
          fontSize: 18,
          color: LANDING_COLORS.green,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

/** Original vs Variation phone mockups */
const MockupComparison = ({ story }) => (
  <Box
    sx={{
      display: 'flex',
      gap: { xs: 2, md: 3 },
      justifyContent: 'center',
      alignItems: 'flex-end',
    }}
  >
    {[
      { label: 'Orijinal', src: story.originalImage },
      { label: 'Varyasyon', src: story.variationImage },
    ].map(({ label, src }) => (
      <Box key={label} sx={{ textAlign: 'center' }}>
        <Box
          component="img"
          src={src}
          alt={`${story.brand} ${label}`}
          sx={{
            width: { xs: 140, sm: 180, md: 225 },
            height: 'auto',
            borderRadius: '6px',
            boxShadow: '0 0 14px rgba(0,0,0,0.1)',
            display: 'block',
            mb: 1,
          }}
        />
        <Typography
          sx={{
            fontFamily: 'Cabin, sans-serif',
            fontSize: 14,
            color: LANDING_COLORS.textGray,
          }}
        >
          {label}
        </Typography>
      </Box>
    ))}
  </Box>
);

/** Text + metrics card */
const StoryContentCard = ({ story, showBrandLabel = false }) => (
  <Box
    sx={{
      border: `1px solid ${LANDING_COLORS.borderLight}`,
      borderRadius: '12px',
      p: { xs: 2.5, md: 3.5 },
      bgcolor: '#fff',
      height: '100%',
    }}
  >
    {showBrandLabel && (
      <Typography
        sx={{
          fontFamily: 'Cabin, sans-serif',
          fontSize: 13,
          color: LANDING_COLORS.textMuted,
          mb: 0.5,
        }}
      >
        {story.brand}
      </Typography>
    )}

    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        mb: 2,
      }}
    >
      <Typography
        component="h3"
        sx={{
          fontFamily: 'Cabin, sans-serif',
          fontWeight: 700,
          fontSize: { xs: 20, md: 24 },
          color: LANDING_COLORS.purple,
          lineHeight: 1.25,
          flex: 1,
        }}
      >
        {story.title}
      </Typography>
      <Box
        component="img"
        src={story.brandLogo}
        alt={story.brand}
        sx={{
          width: story.brandLogoWidth,
          height: story.brandLogoHeight,
          objectFit: 'contain',
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
        }}
      />
    </Box>

    <Typography
      sx={{
        fontFamily: 'Cabin, sans-serif',
        fontSize: { xs: 14, md: 16 },
        color: LANDING_COLORS.textMuted,
        mb: 2,
        lineHeight: 1.65,
      }}
    >
      <Box component="span" sx={{ fontWeight: 600, color: LANDING_COLORS.purple }}>
        Hipotez:
      </Box>{' '}
      {story.hypothesis}
    </Typography>

    <Typography
      sx={{
        fontFamily: 'Cabin, sans-serif',
        fontSize: { xs: 14, md: 16 },
        color: LANDING_COLORS.textMuted,
        mb: 3,
        lineHeight: 1.65,
      }}
    >
      <Box component="span" sx={{ fontWeight: 600, color: LANDING_COLORS.purple }}>
        İş Etkisi:
      </Box>{' '}
      {story.impact}
    </Typography>

    {/* Metrics — desktop: all in one row; mobile: 2+1 layout */}
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        gap: 2,
      }}
    >
      {story.metrics.map((m) => (
        <MetricColumn key={m.label} {...m} />
      ))}
    </Box>

    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        {story.metrics.slice(0, 2).map((m) => (
          <MetricColumn key={m.label} {...m} />
        ))}
      </Box>
      {story.metrics[2] && (
        <MetricColumn {...story.metrics[2]} fullWidth />
      )}
    </Box>
  </Box>
);

const StoryPanel = ({ story, showBrandLabel = false }) => {
  const imagesLeft = story.imagePosition === 'left';

  const mockupsBlock = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: {
          xs: imagesLeft ? 'flex-start' : 'flex-end',
          md: 'center',
        },
      }}
    >
      <MockupComparison story={story} />
    </Box>
  );

  const contentBlock = <StoryContentCard story={story} showBrandLabel={showBrandLabel} />;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: imagesLeft ? 'auto 1fr' : '1fr auto',
        },
        gap: { xs: 3, md: 4 },
        alignItems: { md: 'stretch' },
      }}
    >
      {imagesLeft ? (
        <>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>{mockupsBlock}</Box>
          {contentBlock}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>{mockupsBlock}</Box>
        </>
      ) : (
        <>
          {contentBlock}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>{mockupsBlock}</Box>
        </>
      )}
    </Box>
  );
};

const SuccessStoriesSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeStory = SUCCESS_STORIES[activeIndex];

  return (
    <Box id="stories" component="section" sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fff' }}>
      <Container maxWidth="lg">
        <Typography
          align="center"
          sx={{
            fontFamily: 'Cabin, sans-serif',
            fontWeight: 700,
            fontSize: { xs: 26, md: 36 },
            color: LANDING_COLORS.purple,
            mb: { xs: 3, md: 5 },
          }}
        >
          Başarı hikayeleri
        </Typography>

        <BrandPills activeIndex={activeIndex} onChange={setActiveIndex} />

        {/* Mobile — single story via brand pills */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <StoryPanel story={activeStory} showBrandLabel />
        </Box>

        {/* Desktop — İpragaz left / AgeSA right image layout */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 6 }}>
          {SUCCESS_STORIES.map((story) => (
            <StoryPanel key={story.id} story={story} />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default SuccessStoriesSection;
